import { ReactNode, useMemo } from "react";

import {
    Button,
    Column,
    FONT_SIZES,
    Icon,
    IconButton,
    Image,
    ImageProps,
    PopoverProps,
    PopoverWrapper,
    Row,
    SPACING,
    Span,
    Text,
    useAppPalette,
    usePopover
} from "@bridgesplit/ui";
import { Drawer, drawerClasses, useMediaQuery } from "@mui/material";
import { getAvatarFromName, useLoopscaleWalletContext, usePrivyWalletConnect } from "@bridgesplit/abf-react";
import { useWallets } from "@privy-io/react-auth/solana";
import { formatAddress } from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { AppTracking, TrackEvent } from "app/types";

import ProfileDropdown from "./ProfileDropdown";
import { NAV_BREAKPOINT, NAV_BUTTON_HEIGHT, NAV_ICON_BUTTON_SX } from "./constants";
import { PointsNested } from "./points";

export default function ProfileButton() {
    const {
        data: { userWalletAddress: user },
        isLoading
    } = useLoopscaleWalletContext();
    if (!user || isLoading) return <ConnectButton />;
    return <Connected />;
}

function ConnectButton() {
    const { isLoading } = useLoopscaleWalletContext();
    const isMobile = useMediaQuery(NAV_BREAKPOINT.below);
    const { connectWallet } = usePrivyWalletConnect();
    const { ready } = useWallets();

    if (isMobile) {
        return (
            <IconButton
                icon="connect"
                loading={isLoading}
                sx={NAV_ICON_BUTTON_SX}
                border
                onClick={() => connectWallet()}
                disabled={!ready}
            />
        );
    }

    if (isLoading) {
        return (
            <Button height={NAV_BUTTON_HEIGHT} variant="outlined">
                Connecting...
            </Button>
        );
    }

    return (
        <Button
            height={NAV_BUTTON_HEIGHT}
            variant="outlined"
            tracking={AppTracking.from(TrackEvent.SubmitWalletConnect)}
            onClick={() => connectWallet()}
            disabled={!ready}
        >
            <Icon type="connect" /> {COPY.CONNECT_PROMPT}
        </Button>
    );
}

function Wrapper({ children, ...props }: PopoverProps & { children: ReactNode }) {
    const isMobile = useMediaQuery(NAV_BREAKPOINT.below);
    const { paperBackground } = useAppPalette();

    if (isMobile) {
        return (
            <Drawer
                anchor="right"
                open={!!props.anchorEl}
                sx={{ [`.${drawerClasses.paper}`]: { background: paperBackground, width: "70vw", maxWidth: "300px" } }}
                onClose={props.close}
            >
                <Row spaceBetween sx={{ p: 2, pb: 0 }}>
                    <Row spacing={1}>
                        <Text>
                            <UserInfo />
                        </Text>
                    </Row>

                    <IconButton border={false} icon="reject" onClick={props.close} />
                </Row>
                <PointsNested />
                {children}
            </Drawer>
        );
    }

    return (
        <PopoverWrapper
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ horizontal: "right", vertical: -SPACING }}
            sx={{ maxHeight: "90vh", width: "300px", overflowY: "auto" }}
            {...props}
        >
            <Column>{children}</Column>
        </PopoverWrapper>
    );
}

function Connected() {
    const { popoverProps, close, open } = usePopover();
    const { image, imageVariant } = useUserInfo();
    const isMobile = useMediaQuery(NAV_BREAKPOINT.below);

    return (
        <>
            <Wrapper {...popoverProps}>
                <ProfileDropdown close={close} />
            </Wrapper>
            {(() => {
                if (isMobile) {
                    return (
                        <IconButton sx={NAV_ICON_BUTTON_SX} border onClick={open}>
                            <Image size={FONT_SIZES.body1 + "px"} variant={imageVariant} src={image} />
                        </IconButton>
                    );
                }

                return (
                    <Button height={NAV_BUTTON_HEIGHT} variant="outlined" onClick={open}>
                        <UserInfo />
                    </Button>
                );
            })()}
        </>
    );
}

function UserInfo() {
    const { name, image, imageVariant } = useUserInfo();

    return (
        <>
            {image && <Image size={FONT_SIZES.body1 + "px"} variant={imageVariant} src={image} />}
            <Span>{name}</Span>
        </>
    );
}

function useUserInfo() {
    const {
        data: { userWalletAddress: user, icon, userPublicKey: publicKey }
    } = useLoopscaleWalletContext();

    const image = useMemo(() => {
        if (!user) return undefined;
        if (icon) return icon;
        return getAvatarFromName(user);
    }, [user, icon]);

    const name = useMemo(() => {
        return formatAddress(publicKey);
    }, [publicKey]);

    const imageVariant: ImageProps["variant"] = user ? "square" : "circle";

    return { name, image, imageVariant };
}
